/**
 * Global application state store.
 *
 * Replaces the 25 Wized "variables" with a single React Context that holds
 * reactive state for: cart, modals, filters, saved items, and transient UI
 * selections. Each piece of state has a setter exposed via the context.
 *
 * Improvements over the Wized approach:
 * - Type-safe: every variable has an explicit TypeScript type.
 * - Persistent where needed: cart + saved tutorials + saved bundles survive page reloads via localStorage.
 * - No DOM manipulation: modal state is React state, consumed via conditional rendering.
 * - Centralised: no scattered inline JS strings – one source of truth.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { CartItem } from "./schema";
import {
  readCartFromStorage,
  readSavedFromStorage,
  readSavedBundlesFromStorage,
  writeCartToStorage,
  writeSavedToStorage,
  writeSavedBundlesToStorage,
} from "./helpers";
import { getAddOnIdentityKey, isSameAddOn } from "./addOnIdentity";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ModalType =
  | "create-performance"
  | "save-to-performance"
  | "share-tutorial"
  | "share-playlist"
  | "delete-account"
  | "delete-playlist"
  | "reorder"
  | "change-name"
  | "change-email"
  | "forget-password"
  | "confirm-tutorial-added"
  | "confirm-playlist-created"
  | null;

interface AppState {
  // Cart
  cartItems: CartItem[];
  cartOpen: boolean;

  // Saved / bookmarked course IDs
  savedItems: string[];

  // Saved / bookmarked bundle IDs
  savedBundleItems: string[];

  // Active modal (only one at a time)
  activeModal: ModalType;

  // Transient selections (used when opening modals or navigating)
  selectedCourseShareId: string | null;
  selectedPlaylistShareId: string | null;
  selectedAddToPlaylistCourseId: string | null;
  selectedAddToPlaylistId: string | null;
  selectedDeletePlaylistId: string | null;
  selectedExtendPermissionId: string | null;

  // Search & filter
  librarySearchInput: string;
  dashboardSearchInput: string;
  tagFilter: string[];

  // Video player state
  currentVideo: string | null;
  progressVideoId: string | null;
}

interface AppActions {
  // Cart
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  setCartOpen: (open: boolean) => void;

  // Saved
  toggleSaved: (courseId: string) => void;
  isSaved: (courseId: string) => boolean;
  toggleSavedBundle: (bundleId: string) => void;
  isBundleSaved: (bundleId: string) => boolean;

  // Modals
  openModal: (type: NonNullable<ModalType>) => void;
  closeModal: () => void;

  // Transient selections
  setSelectedCourseShareId: (id: string | null) => void;
  setSelectedPlaylistShareId: (id: string | null) => void;
  setSelectedAddToPlaylistCourseId: (id: string | null) => void;
  setSelectedAddToPlaylistId: (id: string | null) => void;
  setSelectedDeletePlaylistId: (id: string | null) => void;
  setSelectedExtendPermissionId: (id: string | null) => void;

  // Search & filter
  setLibrarySearchInput: (v: string) => void;
  setDashboardSearchInput: (v: string) => void;
  setTagFilter: (tags: string[]) => void;
  addTag: (tag: string) => void;
  removeTag: (tag: string) => void;

  // Video
  setCurrentVideo: (url: string | null) => void;
  setProgressVideoId: (id: string | null) => void;
}

type AppContextValue = AppState & AppActions;

function getCartItemKey(item: CartItem) {
  if (item.type === "course") return `course:${item.course_id}`;
  if (item.type === "bundle") return `bundle:${item.bundle_id}`;
  return `addon:${getAddOnIdentityKey(item)}`;
}

function cartItemIncludesCourse(item: CartItem, courseId: string) {
  if (item.type === "course") return item.course_id === courseId;
  if (item.type === "bundle") return item.course_ids.includes(courseId);
  return false;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AppContext = createContext<AppContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AppProvider({ children }: { children: React.ReactNode }) {
  // --- Persistent state (localStorage) ---
  const [cartItems, setCartItems] = useState<CartItem[]>(readCartFromStorage);
  const [savedItems, setSavedItems] = useState<string[]>(readSavedFromStorage);
  const [savedBundleItems, setSavedBundleItems] = useState<string[]>(readSavedBundlesFromStorage);

  // --- Ephemeral UI state ---
  const [cartOpen, setCartOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  // Transient selections
  const [selectedCourseShareId, setSelectedCourseShareId] = useState<
    string | null
  >(null);
  const [selectedPlaylistShareId, setSelectedPlaylistShareId] = useState<
    string | null
  >(null);
  const [selectedAddToPlaylistCourseId, setSelectedAddToPlaylistCourseId] =
    useState<string | null>(null);
  const [selectedAddToPlaylistId, setSelectedAddToPlaylistId] = useState<
    string | null
  >(null);
  const [selectedDeletePlaylistId, setSelectedDeletePlaylistId] = useState<
    string | null
  >(null);
  const [selectedExtendPermissionId, setSelectedExtendPermissionId] = useState<
    string | null
  >(null);

  // Search & filter
  const [librarySearchInput, setLibrarySearchInput] = useState("");
  const [dashboardSearchInput, setDashboardSearchInput] = useState("");
  const [tagFilter, setTagFilter] = useState<string[]>([]);

  // Video
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);
  const [progressVideoId, setProgressVideoId] = useState<string | null>(null);

  // --- Cart actions ---
  const addToCart = useCallback(
    (item: CartItem) => {
      setCartItems((prev) => {
        const key = getCartItemKey(item);
        const exists = prev.some((existingItem) => {
          if (item.type === "add_on" && existingItem.type === "add_on") {
            return isSameAddOn(existingItem, item);
          }

          return getCartItemKey(existingItem) === key;
        });
        if (exists) return prev;

        if (item.type === "course") {
          const coveredByBundle = prev.some(
            (existingItem) => existingItem.type === "bundle" && existingItem.course_ids.includes(item.course_id),
          );
          if (coveredByBundle) return prev;
        }

        if (item.type === "bundle") {
          const conflictsWithExistingBundle = prev.some(
            (existingItem) =>
              existingItem.type === "bundle" &&
              existingItem.course_ids.some((courseId) => item.course_ids.includes(courseId)),
          );

          if (conflictsWithExistingBundle) return prev;
        }

        const nextBase = item.type === "bundle"
          ? prev.filter(
              (existingItem) =>
                existingItem.type !== "course" || !item.course_ids.includes(existingItem.course_id),
            )
          : prev;

        const next = [...nextBase, item];
        writeCartToStorage(next);
        return next;
      });
    },
    [],
  );

  const removeFromCart = useCallback((id: string) => {
    setCartItems((prev) => {
      const next = prev.filter((item) => getCartItemKey(item) !== id);
      writeCartToStorage(next);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    writeCartToStorage([]);
  }, []);

  // --- Saved actions ---
  const toggleSaved = useCallback((courseId: string) => {
    setSavedItems((prev) => {
      const next = prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId];
      writeSavedToStorage(next);
      return next;
    });
  }, []);

  const isSaved = useCallback(
    (courseId: string) => savedItems.includes(courseId),
    [savedItems],
  );

  const toggleSavedBundle = useCallback((bundleId: string) => {
    setSavedBundleItems((prev) => {
      const next = prev.includes(bundleId)
        ? prev.filter((id) => id !== bundleId)
        : [...prev, bundleId];
      writeSavedBundlesToStorage(next);
      return next;
    });
  }, []);

  const isBundleSaved = useCallback(
    (bundleId: string) => savedBundleItems.includes(bundleId),
    [savedBundleItems],
  );

  // --- Modal actions ---
  const openModal = useCallback((type: NonNullable<ModalType>) => {
    setActiveModal(type);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    // Reset transient selection state when closing any modal
    setSelectedAddToPlaylistId(null);
  }, []);

  // --- Tag filter actions ---
  const addTag = useCallback((tag: string) => {
    setTagFilter((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
  }, []);

  const removeTag = useCallback((tag: string) => {
    setTagFilter((prev) => prev.filter((t) => t !== tag));
  }, []);

  // --- Memoised value ---
  const value = useMemo<AppContextValue>(
    () => ({
      // State
      cartItems,
      cartOpen,
      savedItems,
      savedBundleItems,
      activeModal,
      selectedCourseShareId,
      selectedPlaylistShareId,
      selectedAddToPlaylistCourseId,
      selectedAddToPlaylistId,
      selectedDeletePlaylistId,
      selectedExtendPermissionId,
      librarySearchInput,
      dashboardSearchInput,
      tagFilter,
      currentVideo,
      progressVideoId,

      // Actions
      addToCart,
      removeFromCart,
      clearCart,
      setCartOpen,
      toggleSaved,
      isSaved,
      toggleSavedBundle,
      isBundleSaved,
      openModal,
      closeModal,
      setSelectedCourseShareId,
      setSelectedPlaylistShareId,
      setSelectedAddToPlaylistCourseId,
      setSelectedAddToPlaylistId,
      setSelectedDeletePlaylistId,
      setSelectedExtendPermissionId,
      setLibrarySearchInput,
      setDashboardSearchInput,
      setTagFilter,
      addTag,
      removeTag,
      setCurrentVideo,
      setProgressVideoId,
    }),
    [
      cartItems,
      cartOpen,
      savedItems,
      savedBundleItems,
      activeModal,
      selectedCourseShareId,
      selectedPlaylistShareId,
      selectedAddToPlaylistCourseId,
      selectedAddToPlaylistId,
      selectedDeletePlaylistId,
      selectedExtendPermissionId,
      librarySearchInput,
      dashboardSearchInput,
      tagFilter,
      currentVideo,
      progressVideoId,
      addToCart,
      removeFromCart,
      clearCart,
      toggleSaved,
      isSaved,
      toggleSavedBundle,
      isBundleSaved,
      openModal,
      closeModal,
      addTag,
      removeTag,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAppStore(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppStore must be used within <AppProvider>");
  return ctx;
}
